import { HTMLAttributes, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { api } from '@/api'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/password-input'
import { toast } from '@/hooks/use-toast'

type UserAuthFormProps = HTMLAttributes<HTMLDivElement>

const formSchema = z.object({
  username: z.string().min(1, { message: 'Nhập tên đăng nhập' }),
  password: z.string().min(1, { message: 'Nhập mật khẩu' }),
})

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setUser = useAuthStore((state) => state.auth.setUser)
  const search = useSearch({ strict: false }) as { redirect?: string }
  const [formError, setFormError] = useState<string | null>(null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const login = useMutation({
    mutationFn: (values: z.infer<typeof formSchema>) =>
      api.login(values.username, values.password),
    onSuccess: async (_data, values) => {
      setUser({ username: values.username || 'admin', role: 'admin' })
      queryClient.setQueryData(['auth'], { authenticated: true })
      toast({ title: 'Đăng nhập thành công' })
      const redirectTo =
        typeof search.redirect === 'string' && search.redirect.startsWith('/')
          ? search.redirect
          : '/'
      if (redirectTo === '/') {
        await navigate({ to: '/' })
      } else {
        window.location.assign(redirectTo)
      }
    },
    onError: (error) => {
      setFormError(
        error instanceof Error ? error.message : 'Đăng nhập thất bại'
      )
    },
  })

  function onSubmit(data: z.infer<typeof formSchema>) {
    setFormError(null)
    login.mutate(data)
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='username'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Tên đăng nhập</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete='username'
                      placeholder='admin'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Mật khẩu</FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete='current-password'
                      placeholder='********'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {formError ? (
              <p className='text-sm text-destructive' role='alert'>
                {formError}
              </p>
            ) : null}
            <Button className='mt-2' disabled={login.isPending}>
              {login.isPending ? 'Đang đăng nhập…' : 'Đăng nhập'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
